// Risk matrix editor (Section V) — severity × likelihood → rating + timescales.
import { useState } from "react";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { RATING_COLOR } from "./shared";

export default function RiskMatrix({ rid, matrix, canEdit, onChange }) {
    const [m, setM] = useState(() => JSON.parse(JSON.stringify(matrix || {})));
    const [busy, setBusy] = useState(false);
    if (!m.severities) return null;

    const setCell = (sev, like, rating) => setM((prev) => ({ ...prev, cells: { ...prev.cells, [`${sev}|${like}`]: rating } }));
    const setTimescale = (rating, val) => setM((prev) => ({ ...prev, timescales: { ...prev.timescales, [rating]: val } }));

    async function save() {
        setBusy(true);
        try { await api.patch(`/bsc/${rid}/risk-matrix`, m); toast.success("Risk matrix saved — scenarios re-rated"); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    }

    return (
        <div data-testid="bscr-risk-matrix" className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-[18px] font-bold text-[#1F1F1F]">Managing Risks Summary — Risk Matrix</h2>
                    <p className="text-[12.5px] text-[#6F6A64]">Configure how severity × likelihood maps to a risk rating. Scenarios are rated automatically using this matrix.</p>
                </div>
                {canEdit && <button data-testid="bscr-matrix-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>{busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save matrix</button>}
            </div>

            <div className="overflow-x-auto rounded-xl border border-[#E5E0DA] bg-white">
                <table className="w-full text-[12.5px]">
                    <thead>
                        <tr className="bg-[#F6F4F0] text-[#6F6A64]">
                            <th className="text-left px-4 py-2.5 font-bold">Severity ↓ / Likelihood →</th>
                            {m.likelihoods.map((l) => <th key={l} className="px-3 py-2.5 font-bold">{l}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {m.severities.map((sev) => (
                            <tr key={sev} className="border-t border-[#F0EDE8]">
                                <td className="px-4 py-2.5 font-semibold text-[#1F1F1F]">{sev}</td>
                                {m.likelihoods.map((like) => {
                                    const rating = m.cells[`${sev}|${like}`];
                                    return (
                                        <td key={like} className="px-2 py-1.5 text-center">
                                            {canEdit ? (
                                                <select data-testid={`bscr-matrix-cell-${sev}-${like}`} value={rating || ""} onChange={(e) => setCell(sev, like, e.target.value)}
                                                    className="text-[11.5px] font-semibold rounded-md px-2 py-1.5 border-0 text-white cursor-pointer" style={{ background: RATING_COLOR[rating] || "#9A968F" }}>
                                                    {m.ratings.map((r) => <option key={r} value={r} className="text-black bg-white">{r}</option>)}
                                                </select>
                                            ) : (
                                                <span className="inline-block rounded-md px-2.5 py-1 text-[11.5px] font-semibold text-white" style={{ background: RATING_COLOR[rating] || "#9A968F" }}>{rating}</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="rounded-xl border border-[#E5E0DA] bg-white p-4">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2.5">Action timescales by rating</h4>
                <div className="grid sm:grid-cols-2 gap-2.5">
                    {m.ratings.map((r) => (
                        <div key={r} className="flex items-center gap-2.5">
                            <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ background: RATING_COLOR[r] || "#9A968F" }} />
                            <span className="text-[12.5px] font-semibold w-24 shrink-0">{r}</span>
                            <input data-testid={`bscr-timescale-${r}`} disabled={!canEdit} value={m.timescales?.[r] || ""} onChange={(e) => setTimescale(r, e.target.value)}
                                className="flex-1 px-2.5 py-1.5 rounded-lg border border-[#E5E0DA] text-[12px]" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
